import React, { Key, useEffect, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tooltip,
} from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import { uniqueId } from "lodash";
import { FormInstance } from "antd/es/form/Form";
import { useAppSelector } from "../../../..";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { TableColumn } from "../../../../lib/interfaces";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  toDisplayFormat,
} from "../../../../lib/utils";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import { ITypesCharacteristicsValuesTableRecord } from "./utils/types";
import { getCamelizeDictionaries } from "./utils/getCamelizeDictionaries";
import { ReactComponent as DeleteIcon } from "../../../../img/delete-round.svg";
import { ReactComponent as CopyIcon } from "../../../../img/copy-icon.svg";
import { ReactComponent as DoneIcon } from "../../../../img/done.svg";
import {
  ReactComponent as DeleteBusketIcon,
  ReactComponent as DeleteSavedIcon,
} from "../../../../img/delete-busket.svg";
import { MENU_ADMIN_CHAR_TYPE_WRITE } from "../../../../lib/const/permissions";
import RequiredMark from "../../../../components/common/RequiredMark/RequiredMark";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import "./TypesCharacteristicsValues.less";

const columnsWidths: Partial<Record<keyof ITypesCharacteristicsValuesTableRecord, string>> = {
  valueTypeId: "20%",
  unitTypeId: "20%",
  rangeTypeId: "20%",
  value: "20%",
};

interface IProps {
  mode?: "create" | "view" | "edit_close_date" | "edit";
  typeCharacteristic?: TypeCharacteristic;
  tableData: ITypesCharacteristicsValuesTableRecord[];
  setTableData: (value: React.SetStateAction<ITypesCharacteristicsValuesTableRecord[]>) => void;
  typesCharacteristicsParamsForm?: FormInstance<any>;
  deletedSavedRecord?: (record: ITypesCharacteristicsValuesTableRecord) => void;
  addSavedRecord?: (record: ITypesCharacteristicsValuesTableRecord) => void;
  selectedValueTypeId?: number;
  setSelectedValueTypeId: (value: React.SetStateAction<number | undefined>) => void;
  deletedAllSavedRecord?: (typeCharacteristic: TypeCharacteristic) => void;
}

export const TypesCharacteristicsValues = ({
  mode,
  typeCharacteristic,
  tableData,
  setTableData,
  typesCharacteristicsParamsForm,
  deletedSavedRecord,
  addSavedRecord,
  selectedValueTypeId,
  setSelectedValueTypeId,
  deletedAllSavedRecord,
}: IProps) => {
  const {
    typesCharacteristicsTypesValuesDic,
    typesCharacteristicsTypesValuesDicLoading,
    typesCharacteristicsTypesUnitsDic,
    typesCharacteristicsTypesUnitsDicLoading,
    typesCharacteristicsRangeTypesDic,
    typesCharacteristicsRangeTypesDicLoading,
  } = useAppSelector((state) => state.typesCharacteristics);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  const onRemoveNewRecord = (key: Key) => {
    setTableData((prev) => prev.filter((item) => item.key !== key));
  };

  const onCopyNewRecord = (key: Key) => {
    if (typesCharacteristicsParamsForm) {
      setTableData((prev) => {
        const findRecord = prev.find((item) => item.key === key);
        const newKey = uniqueId("newKey_");

        const values = typesCharacteristicsParamsForm.getFieldsValue();

        typesCharacteristicsParamsForm.setFieldsValue({
          valueTypeId: values.valueTypeId,
          [`${newKey}_unitTypeId`]: values[`${key}_unitTypeId`],
          [`${newKey}_rangeTypeId`]: values[`${key}_rangeTypeId`],
          [`${newKey}_value`]: undefined,
          [`${newKey}_valueTo`]: undefined,
        });

        return [
          ...prev,
          {
            ...findRecord,
            id: Number(uniqueId()),
            key: newKey,
          },
        ];
      });
    }
  };

  const onRemoveAllNewRecord = () => {
    Modal.confirm({
      content: "Вы уверены, что хотите удалить все значения Типа характеристики?",
      cancelText: "Отмена",
      okText: "Подтвердить",
      onOk: () => {
        setTableData([]);
      },
    });
  };

  const onDeleteAllSavedRecord = () => {
    Modal.confirm({
      content: "Вы уверены, что хотите удалить все значения Типа характеристики?",
      cancelText: "Отмена",
      okText: "Подтвердить",
      onOk: () => {
        deletedAllSavedRecord && typeCharacteristic && deletedAllSavedRecord(typeCharacteristic);
      },
    });
  };

  const onCopySavedRecord = (record: ITypesCharacteristicsValuesTableRecord) => {
    if (typesCharacteristicsParamsForm) {
      setTableData((prev) => {
        const findRecord = prev.find((item) => item.key === record.key);
        const newKey = uniqueId("newKey_");

        typesCharacteristicsParamsForm.setFieldsValue({
          [`${newKey}_unitTypeId`]: record.unitTypeId,
          [`${newKey}_rangeTypeId`]: record.rangeTypeId,
          [`${newKey}_value`]: undefined,
          [`${newKey}_valueTo`]: undefined,
          dataTypeId: typeCharacteristic?.dataTypeId,
          valueTypeId: typeCharacteristic?.charTypeValues?.[0]?.valueTypeId,
          valueDicTypeAlias: typeCharacteristic?.valueDicTypeAlias,
        });

        return [
          ...prev,
          {
            ...findRecord,
            id: Number(uniqueId()),
            key: newKey,
          },
        ];
      });
    }
  };

  const onDeleteSavedRecord = (record: ITypesCharacteristicsValuesTableRecord) => {
    deletedSavedRecord && deletedSavedRecord(record);
  };

  const onSaveNewRecord = (record: ITypesCharacteristicsValuesTableRecord) => {
    addSavedRecord && addSavedRecord(record);
  };

  const handleValueTypeIdChange = (e) => {
    tableData.forEach((item) => {
      if (typesCharacteristicsParamsForm) {
        typesCharacteristicsParamsForm.setFieldsValue({
          [`${item.key}_unitTypeId`]: undefined,
          [`${item.key}_rangeTypeId`]: undefined,
          [`${item.key}_value`]: undefined,
          [`${item.key}_valueTo`]: undefined,
        });
      }
    });
    setSelectedValueTypeId(e);
  };

  const findAddedRecord = tableData.find((item) => String(item.key).startsWith("newKey_"));

  const isAddedRecordMode = findAddedRecord && mode === "view";

  const getTypesCharacteristicsValuesColumns = (): TableColumn<ITypesCharacteristicsValuesTableRecord>[] => {
    return [
      {
        title: <RequiredMark>Тип значения</RequiredMark>,
        key: "valueTypeId",
        dataIndex: "valueTypeId",
        width: columnsWidths.valueTypeId,
        hidden: false,
        render: (value, record, idx) => {
          if (mode === "create" || (mode === "view" && String(record.key).startsWith("newKey_"))) {
            return (
              <Form.Item
                initialValue={1}
                name="valueTypeId"
                rules={[{ required: true, message: "" }]}
                className="form-item-required__hidden-error__mb0"
              >
                <Select
                  disabled={idx > 0}
                  loading={checkIsLoading(typesCharacteristicsTypesValuesDicLoading)}
                  onChange={handleValueTypeIdChange}
                >
                  {typesCharacteristicsTypesValuesDic?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }
          return getDictionaryItemById(typesCharacteristicsTypesValuesDic, value)?.name;
        },
      },

      {
        title: "Единица измерения",
        key: "unitTypeId",
        dataIndex: "unitTypeId",
        width: columnsWidths.unitTypeId,
        hidden: false,
        render: (value, record) => {
          if (mode === "create" || (mode === "view" && String(record.key).startsWith("newKey_"))) {
            return (
              <Form.Item
                className="form-item-required__hidden-error__mb0"
                name={`${record.key}_unitTypeId`}
              >
                <Select
                  loading={checkIsLoading(typesCharacteristicsTypesUnitsDicLoading)}
                  allowClear
                >
                  {typesCharacteristicsTypesUnitsDic?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }
          return getDictionaryItemById(typesCharacteristicsTypesUnitsDic, value)?.name;
        },
      },

      {
        title:
          selectedValueTypeId === 2 || selectedValueTypeId === 3 ? (
            <RequiredMark>Тип границ диапазона</RequiredMark>
          ) : (
            "Тип границ диапазона"
          ),
        key: "rangeTypeId",
        dataIndex: "rangeTypeId",
        width: columnsWidths.rangeTypeId,
        hidden: false,
        render: (value, record) => {
          if (mode === "create" || (mode === "view" && String(record.key).startsWith("newKey_"))) {
            return (
              <Form.Item dependencies={["valueTypeId"]} noStyle>
                {({ getFieldValue }) => {
                  const valueTypeId = getFieldValue("valueTypeId");
                  return (
                    <Form.Item
                      name={`${record.key}_rangeTypeId`}
                      rules={[{ required: valueTypeId === 2 || valueTypeId === 3, message: "" }]}
                      className="form-item-required__hidden-error__mb0"
                    >
                      <Select
                        disabled={valueTypeId !== 2 && valueTypeId !== 3}
                        loading={checkIsLoading(typesCharacteristicsRangeTypesDicLoading)}
                      >
                        {typesCharacteristicsRangeTypesDic?.map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            );
          }
          return getDictionaryItemById(typesCharacteristicsRangeTypesDic, value)?.name;
        },
      },

      {
        title: <RequiredMark>Значение (диапазон)</RequiredMark>,
        key: "value",
        dataIndex: "value",
        width: columnsWidths.value,
        render: (value, record) => {
          if (mode === "create" || (mode === "view" && String(record.key).startsWith("newKey_"))) {
            return (
              <Form.Item dependencies={["valueTypeId", "dataTypeId", "valueDicTypeAlias"]} noStyle>
                {({ getFieldValue }) => {
                  const valueTypeId = getFieldValue("valueTypeId");
                  const dataTypeId = getFieldValue("dataTypeId");
                  const valueDicTypeAlias = getFieldValue("valueDicTypeAlias");

                  const isRange = valueTypeId === 2 || valueTypeId === 3;

                  if (dataTypeId === 1) {
                    return (
                      <>
                        {isRange ? (
                          <Row wrap={false}>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_value`}
                              rules={[
                                { required: true, message: "" },
                                { type: "number", message: "Введите число" },
                              ]}
                              style={{ marginRight: 8 }}
                              getValueFromEvent={(e) =>
                                e.target.value ? Number(e.target.value) : undefined
                              }
                            >
                              <Input type="number" placeholder="От" />
                            </Form.Item>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_valueTo`}
                              rules={[
                                { required: true, message: "" },
                                { type: "number", message: "Введите число" },
                              ]}
                              getValueFromEvent={(e) =>
                                e.target.value ? Number(e.target.value) : undefined
                              }
                            >
                              <Input type="number" placeholder="До" />
                            </Form.Item>
                          </Row>
                        ) : (
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name={`${record.key}_value`}
                            rules={[
                              { type: "number", message: "Введите число" },
                              { required: true, message: "" },
                            ]}
                            getValueFromEvent={(e) =>
                              e.target.value ? Number(e.target.value) : undefined
                            }
                          >
                            <Input type="number" placeholder="Введите число" />
                          </Form.Item>
                        )}
                      </>
                    );
                  }
                  if (dataTypeId === 2) {
                    return (
                      <>
                        {isRange ? (
                          <Row wrap={false}>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_value`}
                              rules={[
                                { type: "string", message: "Введите строку" },
                                { required: true, message: "" },
                              ]}
                              style={{ marginRight: 8 }}
                            >
                              <Input type="text" placeholder="От" />
                            </Form.Item>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_valueTo`}
                              rules={[
                                { type: "string", message: "Введите строку" },
                                { required: true, message: "" },
                              ]}
                            >
                              <Input type="text" placeholder="До" />
                            </Form.Item>
                          </Row>
                        ) : (
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name={`${record.key}_value`}
                            rules={[
                              { type: "string", message: "Введите строку" },
                              { required: true, message: "" },
                            ]}
                          >
                            <Input type="text" placeholder="Введите строку" />
                          </Form.Item>
                        )}
                      </>
                    );
                  }
                  if (dataTypeId === 3) {
                    return (
                      <>
                        {isRange ? (
                          <Row wrap={false}>
                            <Form.Item dependencies={[`${record.key}_valueTo`]} noStyle>
                              {({ getFieldValue }) => {
                                const fromDate = getFieldValue(`${record.key}_value`);
                                const toDate = getFieldValue(`${record.key}_valueTo`);

                                return (
                                  <Form.Item
                                    className="form-item-required__hidden-error__mb0"
                                    name={`${record.key}_value`}
                                    rules={[{ required: true, message: "" }]}
                                    style={{ marginRight: 8 }}
                                  >
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      placeholder="От"
                                      format={displayFormat}
                                      disabledDate={(current) => {
                                        return toDate ? current.isAfter(toDate, "day") : false;
                                      }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>

                            <Form.Item dependencies={[`${record.key}_value`]} noStyle>
                              {({ getFieldValue }) => {
                                const fromDate = getFieldValue(`${record.key}_value`);
                                const toDate = getFieldValue(`${record.key}_valueTo`);

                                return (
                                  <Form.Item
                                    className="form-item-required__hidden-error__mb0"
                                    name={`${record.key}_valueTo`}
                                    rules={[{ required: true, message: "" }]}
                                  >
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      placeholder="До"
                                      format={displayFormat}
                                      disabledDate={(current) => {
                                        return fromDate ? current.isBefore(fromDate, "day") : false;
                                      }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Row>
                        ) : (
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name={`${record.key}_value`}
                            rules={[{ required: true, message: "" }]}
                          >
                            <DatePicker style={{ width: "100%" }} format={displayFormat} />
                          </Form.Item>
                        )}
                      </>
                    );
                  }
                  if (dataTypeId === 4) {
                    return (
                      <>
                        {isRange ? (
                          <Row wrap={false}>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_value`}
                              rules={[{ required: true, message: "" }]}
                              style={{ marginRight: 8, width: 200 }}
                            >
                              <Select placeholder="От" maxTagCount={2}>
                                {getCamelizeDictionaries(valueDicTypeAlias)?.map((item) => (
                                  <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_valueTo`}
                              rules={[{ required: true, message: "" }]}
                              style={{ width: 200 }}
                            >
                              <Select placeholder="До">
                                {getCamelizeDictionaries(valueDicTypeAlias)?.map((item) => (
                                  <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Row>
                        ) : (
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name={`${record.key}_value`}
                            rules={[{ required: true, message: "" }]}
                          >
                            <Select
                              mode="multiple"
                              style={{ width: "100%" }}
                              maxTagCount={2}
                              maxTagTextLength={8}
                              placeholder="Выберите значения"
                            >
                              {getCamelizeDictionaries(valueDicTypeAlias)?.map((item) => (
                                <Select.Option key={item.id} value={item.id}>
                                  {item.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                      </>
                    );
                  }
                  if (dataTypeId === 5) {
                    return (
                      <>
                        {isRange ? (
                          <Row wrap={false}>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_value`}
                              rules={[
                                { type: "integer", message: "Введите целое число" },
                                { required: true, message: "" },
                              ]}
                              style={{ marginRight: 8 }}
                              getValueFromEvent={(e) =>
                                e.target.value ? Number(e.target.value) : undefined
                              }
                            >
                              <Input type="number" placeholder="От" step="1" />
                            </Form.Item>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_valueTo`}
                              rules={[
                                { type: "integer", message: "Введите целое число" },
                                { required: true, message: "" },
                              ]}
                              getValueFromEvent={(e) =>
                                e.target.value ? Number(e.target.value) : undefined
                              }
                            >
                              <Input type="number" placeholder="До" step="1" />
                            </Form.Item>
                          </Row>
                        ) : (
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name={`${record.key}_value`}
                            rules={[
                              { type: "integer", message: "Введите целое число" },
                              { required: true, message: "" },
                            ]}
                            getValueFromEvent={(e) =>
                              e.target.value ? Number(e.target.value) : undefined
                            }
                          >
                            <Input type="number" step="1" placeholder="Введите число" />
                          </Form.Item>
                        )}
                      </>
                    );
                  }
                  if (dataTypeId === 6) {
                    return (
                      <>
                        {isRange ? (
                          <Row wrap={false}>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_value`}
                              rules={[
                                { type: "integer", min: 1, message: "Введите натуральное число" },
                                { required: true, message: "" },
                              ]}
                              style={{ marginRight: 8 }}
                              getValueFromEvent={(e) =>
                                e.target.value ? Number(e.target.value) : undefined
                              }
                            >
                              <Input type="number" placeholder="От" min="1" step="1" />
                            </Form.Item>
                            <Form.Item
                              className="form-item-required__hidden-error__mb0"
                              name={`${record.key}_valueTo`}
                              rules={[
                                { type: "integer", min: 1, message: "Введите натуральное число" },
                                { required: true, message: "" },
                              ]}
                              getValueFromEvent={(e) =>
                                e.target.value ? Number(e.target.value) : undefined
                              }
                            >
                              <Input type="number" placeholder="До" min="1" step="1" />
                            </Form.Item>
                          </Row>
                        ) : (
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name={`${record.key}_value`}
                            rules={[
                              { type: "integer", min: 1, message: "Введите натуральное число" },
                              { required: true, message: "" },
                            ]}
                            getValueFromEvent={(e) =>
                              e.target.value ? Number(e.target.value) : undefined
                            }
                          >
                            <Input type="number" min="1" step="1" placeholder="Введите число" />
                          </Form.Item>
                        )}
                      </>
                    );
                  }
                }}
              </Form.Item>
            );
          }

          const dataTypeId = typeCharacteristic?.dataTypeId;

          const isRangeView = record.valueTypeId === 2 || record.valueTypeId === 3;

          if (typeCharacteristic) {
            if (dataTypeId === 4) {
              return isRangeView
                ? `${
                    getDictionaryItemById(
                      getCamelizeDictionaries(typeCharacteristic.valueDicTypeAlias),
                      Number(record.value)
                    )?.name
                  } - ${
                    getDictionaryItemById(
                      getCamelizeDictionaries(typeCharacteristic.valueDicTypeAlias),
                      Number(record.valueTo)
                    )?.name
                  }`
                : getDictionaryItemById(
                    getCamelizeDictionaries(typeCharacteristic.valueDicTypeAlias),
                    Number(record.value)
                  )?.name;
            }
            if (dataTypeId === 3) {
              return isRangeView
                ? `${toDisplayFormat(record?.value)} - ${toDisplayFormat(record?.valueTo)}`
                : toDisplayFormat(value);
            }
            return isRangeView ? `${record?.value} - ${record?.valueTo}` : value;
          }
        },
      },

      {
        title: (
          <Col style={{ textAlign: "right" }}>
            {tableData.length > 0 && mode === "create" && (
              <DeleteIcon
                className="types-characteristics-values-table__deleteButton"
                onClick={onRemoveAllNewRecord}
              />
            )}
            {tableData.length > 0 &&
              mode === "view" &&
              !String(tableData?.[0].key).startsWith("newKey_") &&
              !isAddedRecordMode && (
                <DeleteSavedIcon
                  className="types-characteristics-values-table__deleteSavedButton"
                  onClick={onDeleteAllSavedRecord}
                />
              )}
          </Col>
        ),
        dataIndex: "btns",
        key: "btns",
        width: "100%",
        style: { padding: "10px" },
        render: (v, record, idx) => {
          // Кнопки в режиме создания
          if (mode === "create") {
            return (
              <Col style={{ textAlign: "right" }}>
                <CopyIcon
                  className="types-characteristics-values-table__copyButton"
                  onClick={() => onCopyNewRecord(record.key)}
                />
                <DeleteIcon
                  className="types-characteristics-values-table__deleteButton"
                  onClick={() => onRemoveNewRecord(record.key)}
                />
              </Col>
            );
          }

          // Кнопки в режиме просмотра новой строки
          if (mode === "view" && String(record.key).startsWith("newKey_")) {
            return (
              <Col style={{ textAlign: "right" }}>
                <DoneIcon
                  className="types-characteristics-values-table__doneButtonForView"
                  onClick={() => onSaveNewRecord(record)}
                />
                <DeleteIcon
                  className="types-characteristics-values-table__deleteButtonForView"
                  onClick={() => onRemoveNewRecord(record.key)}
                />
              </Col>
            );
          }

          // Кнопки в режиме просмотра сохранённой строки
          if (mode === "view" && String(record.key).startsWith("savedKey_")) {
            const findAddedRecord = tableData.find((item) =>
              String(item.key).startsWith("newKey_")
            );

            const isAddedRecordMode = findAddedRecord && mode === "view";
            return (
              <Col>
                <Row justify="end">
                  {hasAccess(MENU_ADMIN_CHAR_TYPE_WRITE) && (
                    <>
                      <CopyIcon
                        className="types-characteristics-values-table__copyButton"
                        style={{ cursor: isAddedRecordMode ? "not-allowed" : undefined }}
                        onClick={() => {
                          if (!isAddedRecordMode) onCopySavedRecord(record);
                        }}
                      />
                      <DeleteBusketIcon
                        className="types-characteristics-values-table__deleteBusketButton"
                        onClick={() => onDeleteSavedRecord(record)}
                      />
                    </>
                  )}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      showHistoryModal(Number(record?.id));
                    }}
                    className="iconButton types-characteristics-values-table__threeDotsButton"
                    type="text"
                    htmlType="button"
                    icon={<DotsIcon />}
                  />
                </Row>
              </Col>
            );
          }

          return null;
        },
      },
    ];
  };

  const [columns, setColumns] = useState<TableColumn<ITypesCharacteristicsValuesTableRecord>[]>(
    getTypesCharacteristicsValuesColumns()
  );

  useEffect(() => {
    setColumns(getTypesCharacteristicsValuesColumns());
  }, [
    mode,
    tableData,
    typesCharacteristicsTypesValuesDicLoading,
    typesCharacteristicsTypesUnitsDicLoading,
    typesCharacteristicsRangeTypesDicLoading,
    typeCharacteristic,
    selectedValueTypeId,
  ]);

  const getTableData = () => {
    if (typeCharacteristic) {
      const { charTypeValues } = typeCharacteristic;
      return charTypeValues.map((el) => ({ ...el, key: `savedKey_${el.id}` }));
    }
    return [];
  };

  useEffect(() => {
    setTableData(getTableData());
  }, [typeCharacteristic]);

  const [activeCollapseKey, setActiveCollapseKey] = useState<string | undefined>(undefined);

  const onClickPlus = () => {
    if (typesCharacteristicsParamsForm && typeCharacteristic) {
      typesCharacteristicsParamsForm.setFieldsValue({
        dataTypeId: typeCharacteristic?.dataTypeId,
        valueTypeId: typeCharacteristic?.charTypeValues?.[0]?.valueTypeId,
        valueDicTypeAlias: typeCharacteristic?.valueDicTypeAlias,
      });
    }

    setTableData((prev) => [...prev, { id: Number(uniqueId()), key: uniqueId("newKey_") }]);

    if (activeCollapseKey !== "1") {
      setActiveCollapseKey("1");
    }
  };

  const onChangeCollapse = (key: string | string[]) => {
    if (typeof key === "string") {
      setActiveCollapseKey(key);
    } else {
      setActiveCollapseKey(key[0]);
    }
  };

  return (
    <>
      <Collapse
        style={{ width: "100%" }}
        bordered={false}
        activeKey={activeCollapseKey}
        onChange={onChangeCollapse}
      >
        <Collapse.Panel
          key="1"
          header={
            <Row
              style={{ width: "100%" }}
              justify="space-between"
              className="types-characteristics-create-container__pageTitle"
            >
              <Row style={{ width: "100%" }}>
                <h3 style={{ margin: 0 }}>Значения</h3>
                <Row justify="space-between">
                  <div />
                  {hasAccess(MENU_ADMIN_CHAR_TYPE_WRITE) && (
                    <Form.Item dependencies={["dataTypeId"]} noStyle>
                      {({ getFieldValue }) => {
                        const dataTypeId = getFieldValue("dataTypeId");

                        const findAddedRecord = tableData.find((item) =>
                          String(item.key).startsWith("newKey_")
                        );

                        const isAddedRecordMode = findAddedRecord && mode === "view";

                        // Чтобы + не мигал
                        if (mode === "view" && !typeCharacteristic) {
                          return null;
                        }

                        // Для типа данных словарь (не отображаем)
                        if (dataTypeId === 4 || typeCharacteristic?.dataTypeId === 4) {
                          return null;
                        }

                        return (
                          <Tooltip
                            title={
                              !!dataTypeId || !!typeCharacteristic?.dataTypeId
                                ? undefined
                                : "Выберите Тип данных"
                            }
                          >
                            <Button
                              style={{
                                color: "#00BB77",
                                marginLeft: "5px",
                                marginTop: "-2px",
                                border: "none",
                                background: "transparent",
                              }}
                              shape="circle"
                              className="button-icon"
                              disabled={
                                (!dataTypeId && !typeCharacteristic?.dataTypeId) ||
                                isAddedRecordMode
                              }
                              icon={
                                <PlusCircleFilled
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onClickPlus();
                                  }}
                                />
                              }
                            />
                          </Tooltip>
                        );
                      }}
                    </Form.Item>
                  )}
                </Row>
              </Row>
            </Row>
          }
        >
          <Row className="types-characteristics-values-table">
            <Col span={24}>
              <Table
                size="small"
                columns={columns.filter(({ hidden }) => !hidden)}
                dataSource={tableData}
                pagination={false}
              />
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <HistoryAndPropertyModal
        objectTypeSysname="char_type_value"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_CHAR_TYPE_WRITE)}
      />
    </>
  );
};

export default TypesCharacteristicsValues;
